import React, { useEffect, useRef, useState } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import axiosInstance from '../axiosConfig';
import BeatLoader from "react-spinners/BeatLoader";
import ReactDOM from 'react-dom';
import PopupContent from './PopupContent';
import { useNavigate } from 'react-router-dom';

// Safe initialization with error checking
if (!process.env.REACT_APP_MAPBOX_API_KEY) {
  throw new Error('Missing Mapbox API key - check your environment variables');
}

mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_API_KEY;

// Define constant outside useEffect
const initialCenter = [-98.5795, 39.8283];

const CourseMap = ({ selectedCourse, selectedState, selectedCity, initiationFeeRange }) => {
    const mapContainerRef = useRef(null);
    const mapRef = useRef(null);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();
    const prevFilters = useRef(null);
    const popupNodeRef = useRef(null); // Add ref for popup node

    useEffect(() => {
        const isMobile = window.innerWidth < 768;
        const mapInstance = new mapboxgl.Map({
            container: mapContainerRef.current,
            style: 'mapbox://styles/mapbox/light-v11',
            center: initialCenter,
            zoom: isMobile ? 2.5 : 3.5,
        });

        mapInstance.addControl(new mapboxgl.NavigationControl(), 'top-right');
        mapRef.current = mapInstance;

        const clickHandler = (e) => {
            const properties = e.features[0].properties;
            popupNodeRef.current = document.createElement('div'); // Store in ref

            ReactDOM.render(
                <PopupContent properties={properties} navigate={navigate} />,
                popupNodeRef.current
            );

            new mapboxgl.Popup()
                .setLngLat(e.lngLat)
                .setDOMContent(popupNodeRef.current)
                .addTo(mapInstance);
        };

        mapInstance.on('load', () => {
            setLoading(false);

            mapInstance.addSource('golf_courses', {
                type: 'vector',
                url: 'mapbox://andrewstoops.30d7skcx',
            });

            mapInstance.addLayer({
                id: 'golf-course-layer',
                type: 'circle',
                source: 'golf_courses',
                'source-layer': 'mapbox_wade_hampton-7xgkr1',
                paint: {
                    'circle-color': '#004d40',
                    'circle-radius': 6,
                    'circle-stroke-color': '#ffffff',
                    'circle-stroke-width': 2,
                },
            });

            mapInstance.on('click', 'golf-course-layer', clickHandler);

            return () => {
                mapInstance.off('click', 'golf-course-layer', clickHandler);
                // Cleanup popup node using ref
                if (popupNodeRef.current) {
                    ReactDOM.unmountComponentAtNode(popupNodeRef.current);
                }
            };
        });

        return () => mapInstance.remove();
    }, [navigate]);

    // Updated zoom effect with initialCenter
    useEffect(() => {
        if (mapRef.current) {
            const isMobile = window.innerWidth < 768;
            const map = mapRef.current;

            const fetchAndFly = async (url, zoomLevel) => {
                try {
                    const response = await axiosInstance.get(url);
                    const { latitude, longitude } = response.data;
                    map.flyTo({
                        center: [longitude, latitude],
                        zoom: isMobile ? zoomLevel - 1 : zoomLevel,
                        offset: isMobile ? [0, -250] : [0, 0],
                    });
                } catch (err) {
                    console.error('Error fetching coordinates:', err);
                }
            };

            if (selectedCity) {
                fetchAndFly(`/city-coordinates/${selectedState}/${selectedCity}/`, 12);
            } else if (selectedState) {
                fetchAndFly(`/state-coordinates/${selectedState}/`, 6);
            } else {
                map.flyTo({
                    center: initialCenter,
                    zoom: isMobile ? 2.5 : 3.5,
                    offset: isMobile ? [0, -275] : [0, 0],
                });
            }
        }
    }, [selectedState, selectedCity]);

    // Filter optimization
    useEffect(() => {
        if (mapRef.current && mapRef.current.isStyleLoaded()) {
            const map = mapRef.current;
            const currentFilters = [];

            if (selectedState) {
                currentFilters.push([
                    '==',
                    ['downcase', ['get', 'state']],
                    selectedState.toLowerCase()
                ]);
            }

            if (selectedCity) {
                currentFilters.push([
                    '==',
                    ['downcase', ['get', 'city']],
                    selectedCity.toLowerCase()
                ]);
            }

            currentFilters.push([
                'all',
                ['>=', ['to-number', ['get', 'initiation_fee']], initiationFeeRange[0]],
                ['<=', ['to-number', ['get', 'initiation_fee']], initiationFeeRange[1]]
            ]);

            const newFilter = ['all', ...currentFilters];

            // Only update if filters actually changed
            if (JSON.stringify(newFilter) !== JSON.stringify(prevFilters.current)) {
                map.setFilter('golf-course-layer', newFilter);
                prevFilters.current = newFilter;
            }
        }
    }, [selectedState, selectedCity, initiationFeeRange]);

    // Selected course handling (unchanged)
    useEffect(() => {
        if (mapRef.current && selectedCourse) {
            const isMobile = window.innerWidth < 768;
            const offset = isMobile ? [0, -200] : [0, 0];

            mapRef.current.flyTo({
                center: [selectedCourse.longitude, selectedCourse.latitude],
                zoom: 10,
                offset,
            });
        }
    }, [selectedCourse]);

    return (
        <div style={{ position: 'relative' }}>
            {loading && (
                <div className="map-loader">
                    <BeatLoader size={15} color={"#004d40"} />
                </div>
            )}
            <div ref={mapContainerRef} style={{ width: '100%', height: '100vh' }} />
        </div>
    );
};

export default CourseMap;